import {
  ClampToEdgeWrapping,
  DataTexture,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  PlaneGeometry,
  RGBAFormat,
  Scene,
} from 'three';
import type { WebGLRenderer } from 'three';
import type { PerspectiveCamera } from 'three';
import { BoltView, type BoltVisual } from './BoltView';

/** Fraction of the viewport each letterbox bar covers, top and bottom. */
const END_FRACTION = 0.12;
/** How far the inner edge fades from the bar into the picture, ortho units. */
const EDGE_FADE = 0.05;
/** Texels in the fade ramp. */
const FADE_TEXELS = 32;

const BAR_COLOR = 0x000000;

/**
 * A black alpha ramp for the softening band at the inner edge of each bar:
 * black at one end, fully transparent at the other. A `DataTexture` rather than
 * a canvas keeps the overlay constructible without a DOM (the unit tests run in
 * plain node).
 */
function makeFadeTexture(): DataTexture {
  const data = new Uint8Array(FADE_TEXELS * 4);
  for (let index = 0; index < FADE_TEXELS; index += 1) {
    // v = 0 transparent, v = 1 opaque black.
    data[index * 4 + 3] = Math.round((index / (FADE_TEXELS - 1)) * 255);
  }
  const texture = new DataTexture(data, 1, FADE_TEXELS, RGBAFormat);
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

/**
 * The bullet-time overlay: the focus frame and the pop-out bolts.
 *
 * The frame is a **cinematic letterbox**: solid black bars across the top and
 * bottom only, so the sides stay open. It is drawn *in the engine*, not as CSS,
 * on purpose: it lets the real bolt meshes render on top of it, so a bolt
 * whipping past is drawn with its own colours, glow and trail, crossing the
 * black border rather than a stand-in. Each bar fades only slightly into the
 * picture at its inner edge, so the fight reads through a window without a hard
 * line. The frame is a screen-space pass (an orthographic camera), and the bolts are
 * drawn afterwards with the world camera, through the off-axis frustum, so a
 * bolt crossing the bars is drawn over them and clips out of the viewport
 * naturally while its trail sweeps past. The caller passes every active bolt at
 * its true transform, so there is one continuous bolt for the whole window.
 */
export class BulletTimeOverlay {
  private readonly frameScene = new Scene();
  private readonly ortho = new OrthographicCamera(-1, 1, 1, -1, 0, 10);
  private readonly boltScene = new Scene();
  private readonly bolts: BoltView;
  private readonly plane = new PlaneGeometry(1, 1);
  private readonly barMaterial = new MeshBasicMaterial({
    color: BAR_COLOR,
    depthTest: false,
    depthWrite: false,
  });
  private readonly fadeTexture = makeFadeTexture();
  private readonly fadeMaterial = new MeshBasicMaterial({
    map: this.fadeTexture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  private enabled = false;

  constructor() {
    // A cinematic letterbox: solid-black quads across the top and bottom,
    // sized as fractions of the orthographic [-1, 1] view so they are
    // resolution independent. A fade strip softens each inner edge.
    const bottom = -1 + END_FRACTION * 2;
    const top = 1 - END_FRACTION * 2;
    this.frameScene.add(
      this.bar(0, 1 - END_FRACTION, 2, END_FRACTION * 2),
      this.bar(0, -1 + END_FRACTION, 2, END_FRACTION * 2),
      // The fade strips run from opaque black at the bar's inner edge to
      // transparent into the picture; each is rotated so the gradient points
      // the right way.
      this.fade(0, top - EDGE_FADE / 2, 2, EDGE_FADE, 0),
      this.fade(0, bottom + EDGE_FADE / 2, 2, EDGE_FADE, Math.PI),
    );

    this.bolts = new BoltView(this.boltScene);
    this.setEnabled(false);
  }

  private bar(x: number, y: number, width: number, height: number): Mesh {
    const mesh = new Mesh(this.plane, this.barMaterial);
    mesh.position.set(x, y, -0.5);
    mesh.scale.set(width, height, 1);
    return mesh;
  }

  private fade(x: number, y: number, width: number, height: number, rotation: number): Mesh {
    const mesh = new Mesh(this.plane, this.fadeMaterial);
    mesh.position.set(x, y, -0.4);
    mesh.scale.set(width, height, 1);
    mesh.rotation.z = rotation;
    return mesh;
  }

  /** Show or hide the whole overlay (the window opening and closing). */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.frameScene.visible = enabled;
    if (!enabled) this.bolts.sync([]);
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  /** How many bolts are currently drawn over the frame. */
  get activeCount(): number {
    return this.bolts.activeCount;
  }

  /** How many frame pieces the overlay draws (two bars and two fade edges). */
  get framePieceCount(): number {
    return this.frameScene.children.length;
  }

  /** Point the pop-out at the bolts whipping past (empty to show none). */
  sync(bolts: readonly BoltVisual[]): void {
    this.bolts.sync(this.enabled ? bolts : []);
  }

  /**
   * Draw the overlay over an already-rendered world.
   *
   * The caller renders the world first, then calls this with clearing between
   * passes disabled; it clears the depth buffer so the frame and the bolts sit
   * over the picture, then draws the frame and the bolts in that order.
   */
  render(renderer: WebGLRenderer, camera: PerspectiveCamera): void {
    if (!this.enabled) return;
    renderer.clearDepth();
    renderer.render(this.frameScene, this.ortho);
    renderer.render(this.boltScene, camera);
  }

  dispose(): void {
    this.bolts.dispose();
    this.plane.dispose();
    this.barMaterial.dispose();
    this.fadeMaterial.dispose();
    this.fadeTexture.dispose();
    this.frameScene.clear();
  }
}
